import React, { Fragment, useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
  Badge,
} from "reactstrap";
import Breadcrumb from "../../common/breadcrumb";
import { useSelector } from "react-redux";
import Loader from "../../../shared/Loader";
import { errorToast } from "../../../shared/Toast";
import {
  getSystemProductCategoryListing,
  createSystemProductCategory,
  updateSystemProductCategory,
  deleteSystemProductCategory,
} from "../../../utils/apiCalls";

const SystemProductCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({ name: "", arabicName: "", image: null });

  const userType = localStorage.getItem("userType");
  const storedMerchantId = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const selectedMerchant = userType === "merchant" ? storedMerchantId : reduxSelectedMerchant;

  const fetchCategories = async () => {
    if (!selectedMerchant || selectedMerchant === "all") return;
    setLoading(true);
    try {
      const data = await getSystemProductCategoryListing(selectedMerchant);
      setCategories(data || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [selectedMerchant]);

  const openCreateModal = () => {
    setEditingCategory(null);
    setFormData({ name: "", arabicName: "", image: null });
    setModalOpen(true);
  };

  const openEditModal = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name || "",
      arabicName: category.arabicName || "",
      image: null,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingCategory(null);
    setFormData({ name: "", arabicName: "", image: null });
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      errorToast("Category name is required.");
      return;
    }

    if (!selectedMerchant || selectedMerchant === "all") {
      errorToast("Please select a merchant first.");
      return;
    }

    if (editingCategory) {
      const result = await updateSystemProductCategory(editingCategory.id, formData);
      if (result) {
        closeModal();
        fetchCategories();
      }
    } else {
      const result = await createSystemProductCategory({
        ...formData,
        merchantId: selectedMerchant,
      });
      if (result) {
        closeModal();
        fetchCategories();
      }
    }
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm("Are you sure you want to delete this category?")) return;
    const result = await deleteSystemProductCategory(categoryId);
    if (result) {
      fetchCategories();
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, image: file }));
    }
  };

  return (
    <Fragment>
      <Breadcrumb title="System Product Categories" parent="Products" />
      <Container fluid={true}>
        <Row>
          <Col sm="12">
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h5>System Product Categories</h5>
                <Button color="primary" onClick={openCreateModal}>
                  Add Category
                </Button>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <Loader />
                ) : !selectedMerchant || selectedMerchant === "all" ? (
                  <div className="text-center text-muted py-4">
                    Please select a merchant to view system product categories.
                  </div>
                ) : categories.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    No system product categories found. Create one to get started.
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table className="table-border-horizontal">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Name</th>
                          <th>Arabic Name</th>
                          <th>Products</th>
                          <th>Status</th>
                          <th>Sort Order</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {categories.map((cat, index) => (
                          <tr key={cat.id}>
                            <td>{index + 1}</td>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                {cat.image && (
                                  <img
                                    src={process.env.REACT_APP_URL + cat.image}
                                    alt={cat.name}
                                    style={{
                                      width: 36,
                                      height: 36,
                                      borderRadius: 6,
                                      objectFit: "cover",
                                      marginRight: 8,
                                    }}
                                  />
                                )}
                                {cat.name}
                              </div>
                            </td>
                            <td style={{ direction: "rtl" }}>{cat.arabicName || "-"}</td>
                            <td>{cat.productCount || 0}</td>
                            <td>
                              <Badge color={cat.is_active ? "success" : "secondary"}>
                                {cat.is_active ? "Active" : "Inactive"}
                              </Badge>
                            </td>
                            <td>{cat.sortOrder}</td>
                            <td>
                              <Button
                                color="info"
                                size="sm"
                                className="me-2"
                                onClick={() => openEditModal(cat)}
                              >
                                Edit
                              </Button>
                              <Button
                                color="danger"
                                size="sm"
                                onClick={() => handleDelete(cat.id)}
                              >
                                Delete
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Modal isOpen={modalOpen} toggle={closeModal}>
          <ModalHeader toggle={closeModal}>
            {editingCategory ? "Edit Category" : "Add Category"}
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label>Category Name:</Label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter category name"
              />
            </FormGroup>
            <FormGroup>
              <Label>Arabic Name:</Label>
              <Input
                type="text"
                value={formData.arabicName}
                onChange={(e) => setFormData({ ...formData, arabicName: e.target.value })}
                style={{ direction: "rtl" }}
                placeholder="Enter Arabic name"
              />
            </FormGroup>
            <FormGroup>
              <Label>Category Image:</Label>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <label
                  style={{
                    backgroundColor: "#dc3545",
                    color: "white",
                    padding: "8px 16px",
                    borderRadius: "4px",
                    cursor: "pointer",
                    display: "inline-block",
                    fontSize: "14px",
                  }}
                >
                  Choose Image
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    style={{ display: "none" }}
                  />
                </label>
                <span style={{ color: "#666", fontSize: "14px" }}>
                  {formData.image ? formData.image.name : "No file chosen"}
                </span>
              </div>
              {formData.image && (
                <div style={{ marginTop: "15px", position: "relative", display: "inline-block" }}>
                  <img
                    src={URL.createObjectURL(formData.image)}
                    alt="Preview"
                    style={{
                      maxWidth: "120px",
                      maxHeight: "120px",
                      borderRadius: "8px",
                      border: "1px solid #ddd",
                      objectFit: "cover",
                    }}
                  />
                  <button
                    onClick={() => setFormData((prev) => ({ ...prev, image: null }))}
                    style={{
                      position: "absolute",
                      top: "-10px",
                      right: "-10px",
                      backgroundColor: "#dc3545",
                      color: "white",
                      border: "none",
                      borderRadius: "50%",
                      width: "24px",
                      height: "24px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      fontSize: "12px",
                    }}
                  >
                    x
                  </button>
                </div>
              )}
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button
              style={{
                backgroundColor: "#dc3545",
                borderColor: "#dc3545",
                borderRadius: "4px",
                padding: "8px 20px",
                fontSize: "14px",
              }}
              onClick={handleSubmit}
            >
              {editingCategory ? "Update" : "Save"}
            </Button>
            <Button
              style={{
                backgroundColor: "#6c757d",
                borderColor: "#6c757d",
                borderRadius: "4px",
                padding: "8px 20px",
                fontSize: "14px",
              }}
              onClick={closeModal}
            >
              Close
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </Fragment>
  );
};

export default SystemProductCategories;
